import * as React from 'react';

import { enumOperationType } from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  ContentSection,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import {
  counterpartyGroupCreateAtom,
  counterpartyGroupUpdateAtom,
} from '../../_graphql/mutationCounterpartyGroupCrud';
import * as state from '../../_state/counterpartyGroupsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { CounterpartyGroupRecord } from '../../types/pricing';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';

interface CounterpartyGroupFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  group?: CounterpartyGroupRecord;
}

//
// ⚛️ ⚛️ ⚛️ COUNTERPARTY GROUP FORM DIALOG ⚛️ ⚛️ ⚛️
//
export const CounterpartyGroupFormDialog = ({
  isOpen,
  onClose,
  onSuccess,
  group,
}: CounterpartyGroupFormDialogProps) => {
  // Translations
  const t = useTranslations();

  const isEditing = !!group;

  // States
  const [name, setName] = React.useState(group?.name ?? '');
  const [operationType, setOperationType] = React.useState<string>(
    group?.operationType ?? 'Purchase',
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Data hooks
  const toast = useToast();
  const { refetch } = useAtomValue(state.$remoteCounterpartyGroups);
  const createGroup = useSetAtom(counterpartyGroupCreateAtom({}));
  const updateGroup = useSetAtom(counterpartyGroupUpdateAtom({}));

  // Callbacks
  const handleClose = React.useCallback(() => {
    setName('');
    setOperationType('Purchase');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!name.trim()) {
      toast.error(t.pricing.counterpartyGroups.validation.nameRequired);
      return;
    }

    const operationTypeMap = {
      Purchase: enumOperationType.Purchase,
      Sale: enumOperationType.Sale,
    } as const;

    try {
      setIsSubmitting(true);
      const resolvedOperationType =
        operationTypeMap[operationType as keyof typeof operationTypeMap] ??
        enumOperationType.Brokerage;

      if (isEditing && group) {
        await updateGroup({
          id: group.id,
          name: name.trim(),
          operationType: resolvedOperationType,
        });
        toast.success(t.pricing.counterpartyGroups.toast.updated);
      } else {
        await createGroup({
          name: name.trim(),
          operationType: resolvedOperationType,
        });
        toast.success(t.pricing.counterpartyGroups.toast.created);
      }
      refetch?.();
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(
        isEditing
          ? t.pricing.counterpartyGroups.toast.updateError(errorMessage)
          : t.pricing.counterpartyGroups.toast.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    createGroup,
    updateGroup,
    name,
    operationType,
    isEditing,
    group,
    toast,
    refetch,
    onSuccess,
    handleClose,
    t,
  ]);

  // Effects
  React.useEffect(() => {
    if (isOpen) {
      setName(group?.name ?? '');
      setOperationType(group?.operationType ?? 'Purchase');
    }
  }, [isOpen, group]);

  const canSubmit = Boolean(name.trim()) && !isSubmitting;

  let submitLabel = isEditing
    ? t.pricing.counterpartyGroups.edit.submitButton
    : t.pricing.counterpartyGroups.create.submitButton;
  if (isSubmitting) submitLabel = t.common.loading.creating;

  return (
    <ConsoleDialog
      className="@CounterpartyGroupFormDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={
        isEditing
          ? t.pricing.counterpartyGroups.edit.title
          : t.pricing.counterpartyGroups.create.title
      }
      contentClassName="px-0"
      content={
        <div className="space-y-6">
          <ContentSection
            variant="panel"
            hideDivider
            title={t.pricing.counterpartyGroups.create.sections.groupDetails}
          >
            <div className="grid gap-4">
              <TextField
                label={t.pricing.counterpartyGroups.create.fields.name}
                value={name}
                onChange={setName}
                placeholder={
                  t.pricing.counterpartyGroups.create.placeholders.name
                }
                isRequired
              />
              <SelectEnum
                enumValue={enumOperationType}
                label={t.pricing.counterpartyGroups.create.fields.operationType}
                value={operationType}
                onChange={(value) => setOperationType(value ?? 'Purchase')}
              />
            </div>
          </ContentSection>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      }
    />
  );
};
